const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

export const fmt = (n) =>
  typeof n === "number" && Number.isFinite(n) ? n.toLocaleString("en-IN") : "—";

export const pct = (v, digits = 1) =>
  typeof v === "number" && Number.isFinite(v) ? `${v.toFixed(digits)}%` : "—";

export const formatCompactK = (n) => {
  if (typeof n !== "number" || !Number.isFinite(n)) return "—";
  if (n >= 1000) return `${Math.round(n / 1000)}k`;
  return fmt(n);
};

export const formatCompactKOne = (n) => {
  if (typeof n !== "number" || !Number.isFinite(n)) return "—";
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return fmt(n);
};

export const formatLakhShort = (n) => {
  if (typeof n !== "number" || !Number.isFinite(n)) return "—";
  if (n >= 100000) return `${(n / 100000).toFixed(2)}L`;
  return fmt(n);
};

export const buildAccuracyGaugeStyle = (accuracy = 0) => ({
  background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(#10b981 0 ${accuracy}%, rgba(16,185,129,.14) ${accuracy}% 100%)`,
});

export const buildQualityDonutStyle = (validPct = 0, errorPct = 0) => {
  const validEnd = validPct;
  const errorEnd = validEnd + errorPct;
  return {
    background: `conic-gradient(#10b981 0 ${validEnd}%, #ef4444 ${validEnd}% ${errorEnd}%, #f59e0b ${errorEnd}% 100%)`,
  };
};

export const buildHeroTitle = (accuracy = 0) => {
  if (accuracy >= 85) return "Good data quality, with targeted cleanup needed";
  if (accuracy >= 70) return "Moderate data quality — focused cleanup and reporting needed";
  return "Data quality needs urgent attention across blocks";
};

export const buildHeroNarrative = (overview = {}, dataQuality = {}) => {
  const validPct = dataQuality.valid_pct ?? 0;
  const reporting = overview.reporting_rate ?? 0;
  return `Most remarks are valid (${pct(validPct)}), but wrong/duplicate entries and pending imports need a focused cleanup sprint. Reporting coverage is ${reporting >= 70 ? "moderate" : "low"} at ${pct(reporting)}, so improving school participation should stay on the executive agenda.`;
};

const CATEGORY_DISPLAY = [
  { match: /class 12 passed/i, label: "Class 12 Passed", sub: "Largest clean category", barClass: "green" },
  { match: /wrong entry/i, label: "Wrong Entry / Duplicate", sub: "Cleanup queue", barClass: "red" },
  { match: /migration \(domestic\)/i, label: "Migration Domestic", sub: "Movement remarks", barClass: "" },
  { match: /iti/i, label: "ITI / Polytechnic", sub: "Education transition", barClass: "purple" },
  { match: /open schooling/i, label: "Open Schooling", sub: "Alternative pathway", barClass: "purple" },
  { match: /active for import|pending import/i, label: "Pending Import", sub: "Needs processing", barClass: "orange" },
  { match: /drop out/i, label: "Drop Out", sub: "Follow-up cases", barClass: "red" },
];

export const buildCategoryBars = (categoryData = []) => {
  const rows = CATEGORY_DISPLAY.map((meta) => {
    const row = categoryData.find((c) => meta.match.test(c.name || ""));
    return {
      ...meta,
      value: row?.value ?? 0,
    };
  }).filter((r) => r.value > 0);

  const max = Math.max(...rows.map((r) => r.value), 1);
  return rows.map((r) => ({
    ...r,
    widthPct: clamp((r.value / max) * 100, 4, 100),
    displayValue: r.value >= 100000 ? formatLakhShort(r.value) : fmt(r.value),
  }));
};

export const buildExecutiveInsights = (overview = {}, dataQuality = {}) => {
  const validPct = dataQuality.valid_pct ?? 0;
  const nonReporting = Math.max(
    0,
    (overview.total_schools ?? 0) - (overview.schools_with_remarks ?? 0)
  );
  const wrong = overview.wrong_entry ?? dataQuality.error_count ?? 0;
  const dropout = overview.dropout ?? 0;

  return [
    {
      id: "valid",
      title: "Valid Data Dominates",
      body: `${pct(validPct)} remarks are clean and usable for reporting, planning and follow-up.`,
      iconClass: "green",
    },
    {
      id: "reporting",
      title: "Reporting Gap Exists",
      body: `${fmt(nonReporting)} schools are not yet represented in reporting, reducing coverage confidence.`,
      iconClass: "orange",
    },
    {
      id: "cleanup",
      title: "Cleanup Queue Is Material",
      body:
        wrong > dropout
          ? "Wrong/duplicate entries are larger than dropout remarks and should be resolved first."
          : "Wrong/duplicate entries remain a priority cleanup queue alongside dropout follow-up.",
      iconClass: "red",
    },
    {
      id: "load",
      title: "Load Is Block-Concentrated",
      body: "Top blocks carry large operational volume, making prioritised monitoring more practical.",
      iconClass: "purple",
    },
  ];
};

const LOAD_LABELS = ["Highest load", "High load", "High load", "Medium-high", "Medium-high", "Medium", "Medium", "Watch", "Watch", "Watch"];

export const buildTopBlocks = (blockData = []) => {
  const sorted = [...blockData].sort((a, b) => (b.total_remarks ?? 0) - (a.total_remarks ?? 0)).slice(0, 10);
  const max = sorted[0]?.total_remarks ?? 1;
  return sorted.map((block, idx) => ({
    rank: idx + 1,
    block_name: block.block_name,
    block_code: block.block_code,
    total_remarks: block.total_remarks ?? 0,
    loadLabel: LOAD_LABELS[idx] ?? "Watch",
    barPct: clamp(((block.total_remarks ?? 0) / max) * 100, 8, 100),
    displayRemarks: formatCompactK(block.total_remarks ?? 0),
  }));
};

const FOCUS_TEMPLATES = [
  "Duplicate cleanup + dropout follow-up",
  "Pending import closure",
  "School reporting coverage",
  "Data validation queue",
  "Transition remark validation",
];

export const suggestBlockFocus = (block = {}, rank = 1) => {
  const total = block.total_remarks || 1;
  const wrong = block.wrong_entry ?? 0;
  const pending = block.active_import ?? 0;
  if (wrong / total > 0.12 || (rank === 1 && wrong > 5000)) return FOCUS_TEMPLATES[0];
  if (pending / total > 0.05 || rank === 2) return FOCUS_TEMPLATES[1];
  if ((block.dropout_pct ?? 0) > 2.8) return "Dropout follow-up";
  return FOCUS_TEMPLATES[(rank - 1) % FOCUS_TEMPLATES.length];
};

export const buildBlockWorkloadSummary = (blockData = []) => {
  const top = buildTopBlocks(blockData).slice(0, 5);
  return top.map((row) => {
    const block = blockData.find((b) => b.block_name === row.block_name) ?? {};
    const priority = row.rank <= 2 ? "High" : "Medium";
    return {
      ...row,
      priority,
      priorityClass: priority === "High" ? "red" : "orange",
      focus: suggestBlockFocus(block, row.rank),
    };
  });
};

export const buildManagementProfile = (overview = {}, managementWiseData = []) => {
  const hasMgmt =
    managementWiseData.some((m) => (m.school_count ?? 0) > 0 || (m.total_remarks ?? 0) > 0);
  if (hasMgmt) {
    const top = [...managementWiseData]
      .filter((m) => (m.total_remarks ?? 0) > 0)
      .sort((a, b) => (b.total_remarks ?? 0) - (a.total_remarks ?? 0))[0];
    const share =
      overview.total_remarks > 0
        ? Math.round(((top?.total_remarks ?? 0) / overview.total_remarks) * 100)
        : 100;
    return {
      label: top?.management ?? "Other",
      sharePct: share,
      needsClassification: false,
      rows: managementWiseData.filter((m) => (m.school_count ?? 0) > 0),
    };
  }

  return {
    label: "Other",
    sharePct: 100,
    needsClassification: true,
    rows: [
      {
        management: "Other",
        school_count: overview.total_schools ?? 0,
        total_remarks: overview.total_remarks ?? 0,
        avg_remarks_per_school: overview.avg_remarks_per_school ?? 0,
        dropout: overview.dropout ?? 0,
        dropout_pct: overview.dropout_pct ?? 0,
      },
    ],
  };
};

export const buildActionQueue = (overview = {}) => {
  const wrong = overview.wrong_entry ?? 0;
  const pending = overview.active_import ?? 0;
  const reporting = overview.reporting_rate ?? 0;
  return [
    {
      title: "Clean Wrong and Duplicate Records",
      body: `Start with the ${fmt(wrong)} record cleanup queue before interpreting risk patterns.`,
      iconClass: "red",
    },
    {
      title: "Close Pending Import Backlog",
      body: `Assign block owners for ${fmt(pending)} pending import records and track closure by week.`,
      iconClass: "orange",
    },
    {
      title: "Improve Reporting Coverage",
      body: `Move coverage from ${pct(reporting)} toward 80% by following up with non-reporting schools.`,
      iconClass: "purple",
    },
  ];
};

export const metricProgressWidths = (overview = {}) => {
  const total = overview.total_remarks || 1;
  return {
    schools: 100,
    reporting: overview.reporting_rate ?? 0,
    remarks: clamp((overview.avg_remarks_per_school ?? 0) * 1.2, 40, 100),
    dropout: clamp((overview.dropout_pct ?? 0) * 10, 8, 100),
    pending: clamp(((overview.active_import ?? 0) / total) * 200, 10, 100),
    wrong: clamp(((overview.wrong_entry ?? 0) / total) * 300, 12, 100),
  };
};

export const sortBlocksByRemarks = (blockData = []) =>
  [...blockData].sort((a, b) => (b.total_remarks ?? 0) - (a.total_remarks ?? 0));

const titleCaseBlock = (name = "") =>
  name
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");

export const getBlockErrorStatus = (errorPct = 0) => {
  if (errorPct <= 5) return { label: "Healthy", badgeClass: "green" };
  if (errorPct <= 15) return { label: "Watch", badgeClass: "amber" };
  return { label: "Critical", badgeClass: "red" };
};

export const getErrorRiskClass = (errorPct = 0) => {
  if (errorPct <= 5) return "good";
  if (errorPct <= 15) return "warn";
  return "bad";
};

export const errorHeatlinePosition = (errorPct = 0) =>
  clamp(Math.round((errorPct / 30) * 100), 6, 94);

export const buildBlockHeroCopy = (sortedBlocks = []) => {
  const topLoad = sortedBlocks[0];
  const secondLoad = sortedBlocks[1];
  const highError = [...sortedBlocks].sort((a, b) => (b.error_pct ?? 0) - (a.error_pct ?? 0)).slice(0, 3);
  const errorNames = highError.map((b) => titleCaseBlock(b.block_name)).join(", ");
  return `Prioritize action by separating high-volume blocks from high-error blocks. ${titleCaseBlock(topLoad?.block_name ?? "—")} and ${titleCaseBlock(secondLoad?.block_name ?? "—")} drive the largest remark load, while ${errorNames || "selected blocks"} need immediate data-quality review.`;
};

export const buildBlockHeroStats = (blockData = [], overview = {}) => {
  const sorted = sortBlocksByRemarks(blockData);
  const highErrorCount = blockData.filter((b) => (b.error_pct ?? 0) > 15).length;
  const totalDropout = blockData.reduce((sum, b) => sum + (b.dropout ?? 0), 0) || overview.dropout || 0;
  const totalRemarks = blockData.reduce((sum, b) => sum + (b.total_remarks ?? 0), 0) || overview.total_remarks || 0;

  const byAvg = [...blockData].sort((a, b) => (b.avg_remarks_per_school ?? 0) - (a.avg_remarks_per_school ?? 0));
  const byErrorHigh = [...blockData].sort((a, b) => (b.error_pct ?? 0) - (a.error_pct ?? 0));
  const byErrorLow = [...blockData].sort((a, b) => (a.error_pct ?? 0) - (b.error_pct ?? 0));

  const minAvg = byAvg[byAvg.length - 1];
  const maxAvg = byAvg[0];

  return {
    blockCount: blockData.length,
    highErrorCount,
    totalDropout,
    totalRemarks,
    rangeLow: minAvg,
    rangeHigh: maxAvg,
    kpis: [
      {
        label: "Highest Remark Load",
        value: fmt(sorted[0]?.total_remarks),
        sub: `${titleCaseBlock(sorted[0]?.block_name)} · ${fmt(sorted[0]?.total_schools)} schools`,
        subClass: "blue-text",
      },
      {
        label: "Highest Avg/school",
        value: maxAvg?.avg_remarks_per_school ?? "—",
        sub: `${titleCaseBlock(maxAvg?.block_name)} · intensity hotspot`,
        subClass: "blue-text",
      },
      {
        label: "Highest Error Risk",
        value: pct(byErrorHigh[0]?.error_pct ?? 0),
        sub: `${titleCaseBlock(byErrorHigh[0]?.block_name)} · data validation priority`,
        subClass: "red-text",
      },
      {
        label: "Lowest Error Risk",
        value: pct(byErrorLow[0]?.error_pct ?? 0),
        sub: `${titleCaseBlock(byErrorLow[0]?.block_name)} · strong data discipline`,
        subClass: "green-text",
      },
    ],
  };
};

export const buildBlockPerformanceInsights = (blockData = []) => {
  const sorted = sortBlocksByRemarks(blockData);
  const top5 = sorted.slice(0, 5).map((b) => titleCaseBlock(b.block_name));
  const highErrorSmall = [...blockData]
    .filter((b) => (b.error_pct ?? 0) > 15)
    .sort((a, b) => (a.total_remarks ?? 0) - (b.total_remarks ?? 0))
    .slice(0, 2);
  const benchmark = [...blockData]
    .filter((b) => (b.error_pct ?? 0) <= 5 && (b.total_remarks ?? 0) > 30000)
    .sort((a, b) => (b.total_remarks ?? 0) - (a.total_remarks ?? 0))[0];

  return [
    {
      id: "volume",
      title: "Top 5 Blocks Carry the Operational Load",
      body: `${top5.join(", ")} together dominate remarks volume and should be first in review sequencing.`,
      iconClass: "",
      pill: "Volume",
      pillClass: "",
    },
    {
      id: "quality",
      title: "Error Risk Is Not Proportional to Volume",
      body: highErrorSmall.length
        ? `${highErrorSmall.map((b) => titleCaseBlock(b.block_name)).join(" and ")} have smaller remark loads but high error percentages, indicating a data-entry quality rather than workload issue.`
        : "Several blocks show high error percentages despite lower remark volume, indicating data-entry quality issues.",
      iconClass: "red",
      pill: "Quality",
      pillClass: "red",
    },
    {
      id: "benchmark",
      title: "Haveli Sets a Useful Control Benchmark",
      body: benchmark
        ? `${titleCaseBlock(benchmark.block_name)} combines material volume with only ${pct(benchmark.error_pct)} error risk, making it a strong process benchmark for other blocks.`
        : "Identify low-error blocks with meaningful volume to use as process benchmarks for district review.",
      iconClass: "green",
      pill: "Benchmark",
      pillClass: "green",
    },
  ];
};

export const buildLoadRanking = (blockData = [], limit = 7) => {
  const sorted = sortBlocksByRemarks(blockData).slice(0, limit);
  const max = sorted[0]?.total_remarks ?? 1;
  return sorted.map((block) => ({
    block_name: block.block_name,
    block_code: block.block_code,
    label: titleCaseBlock(block.block_name),
    value: block.total_remarks ?? 0,
    displayValue: fmt(block.total_remarks),
    widthPct: clamp(((block.total_remarks ?? 0) / max) * 100, 18, 100),
  }));
};

export const buildErrorHeatline = (blockData = [], limit = 6) => {
  return [...blockData]
    .sort((a, b) => (b.error_pct ?? 0) - (a.error_pct ?? 0))
    .slice(0, limit)
    .map((block) => ({
      block_name: block.block_name,
      label: titleCaseBlock(block.block_name),
      error_pct: block.error_pct ?? 0,
      displayPct: Math.round(block.error_pct ?? 0),
      position: errorHeatlinePosition(block.error_pct ?? 0),
      riskClass: getErrorRiskClass(block.error_pct ?? 0),
    }));
};

export const buildBlockRankingTable = (blockData = []) =>
  sortBlocksByRemarks(blockData).map((block, idx) => ({
    rank: idx + 1,
    ...block,
    status: getBlockErrorStatus(block.error_pct ?? 0),
  }));

export const buildCategoryMix = (blockData = [], limit = 8) => {
  const sorted = sortBlocksByRemarks(blockData).slice(0, limit);
  return sorted.map((block) => {
    const total = block.total_remarks || 1;
    const segments = [
      { key: "class12_passed", className: "seg-green", value: block.class12_passed ?? 0 },
      { key: "migrated_domestic", className: "seg-blue", value: block.migrated_domestic ?? 0 },
      { key: "iti_polytechnic", className: "seg-purple", value: block.iti_polytechnic ?? 0 },
      { key: "dropout", className: "seg-red", value: block.dropout ?? 0 },
    ].map((seg) => ({
      ...seg,
      widthPct: clamp((seg.value / total) * 100, 0, 100),
    }));
    return {
      block_name: block.block_name,
      label: titleCaseBlock(block.block_name),
      total: block.total_remarks ?? 0,
      displayTotal: formatCompactKOne(block.total_remarks ?? 0),
      segments,
    };
  });
};

export const shortSchoolName = (name = "", max = 22) => {
  const trimmed = (name || "").trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max).trim()}...`;
};

const shortActionSchoolName = (name = "") => {
  const words = (name || "").trim().split(/\s+/).filter(Boolean);
  if (words.length <= 2) return words.join(" ");
  if (/^V\s*P/i.test(words[0])) return `${words[0]} ${words[1] || ""}`.trim();
  return words[0];
};

export const getSchoolStatus = (school = {}) => {
  const errors = school.wrong_entry ?? 0;
  const dropout = school.dropout ?? 0;

  if (errors > 250) {
    return {
      label: "High Error",
      tagClass: "red",
      qualityClass: "danger",
      chipText: `${errors} errors`,
    };
  }
  if (dropout > 50) {
    return {
      label: "Dropout",
      tagClass: dropout > 100 ? "red" : "orange",
      qualityClass: "danger",
      chipText: `${dropout} dropout`,
    };
  }
  if (errors <= 4 && dropout <= 2) {
    return { label: "Clean", tagClass: "green", qualityClass: "", chipText: "Clean" };
  }
  if (errors >= 8 && errors <= 10 && dropout === 0) {
    return { label: "Monitor", tagClass: "", qualityClass: "", chipText: `${errors} errors` };
  }
  if (errors > 10 || dropout > 0) {
    return {
      label: "Review",
      tagClass: "orange",
      qualityClass: "warning",
      chipText: errors ? `${errors} errors` : `${dropout} dropout`,
    };
  }
  return { label: "Clean", tagClass: "green", qualityClass: "", chipText: "Clean" };
};

export const buildSchoolHeroStats = (topSchools = []) => {
  const totalRemarks = topSchools.reduce((sum, s) => sum + (s.total_remarks ?? 0), 0);
  const class12 = topSchools.reduce((sum, s) => sum + (s.class12_passed ?? 0), 0);
  const errors = topSchools.reduce((sum, s) => sum + (s.wrong_entry ?? 0), 0);
  const dropout = topSchools.reduce((sum, s) => sum + (s.dropout ?? 0), 0);
  const class12Pct = totalRemarks ? (class12 / totalRemarks) * 100 : 0;
  const errorPct = totalRemarks ? (errors / totalRemarks) * 100 : 0;
  const topSchool = topSchools[0];

  return {
    totalRemarks,
    class12,
    class12Pct,
    errors,
    errorPct,
    dropout,
    topSchool,
    topErrors: topSchool?.wrong_entry ?? 0,
    chips: [
      { text: `Top school clean: ${topSchool?.wrong_entry ?? 0} errors`, className: "green" },
      { text: `${fmt(errors)} errors in top 20`, className: "orange" },
      { text: `${fmt(dropout)} dropout remarks`, className: "red" },
      { text: `${pct(class12Pct)} class 12 transition share`, className: "" },
    ],
    kpis: [
      {
        label: "Top 20 Remarks",
        value: fmt(totalRemarks),
        sub: "Concentrated in highest-load schools",
        subClass: "",
      },
      {
        label: "Class 12 Passed",
        value: fmt(class12),
        sub: `${pct(class12Pct)} of top 20 remarks`,
        subClass: "positive",
      },
      {
        label: "Data Errors",
        value: fmt(errors),
        sub: `${pct(errorPct)} correction load`,
        subClass: "warning",
      },
      {
        label: "Dropout Remarks",
        value: fmt(dropout),
        sub: "Needs school-level verification",
        subClass: "danger",
      },
    ],
  };
};

export const buildSchoolHeroCopy = (topSchools = []) => {
  const top = topSchools[0];
  if (!top) return "No school data available for deep dive analysis.";
  const name = top.school_name || "Top school";
  const errors = top.wrong_entry ?? 0;
  const dropout = top.dropout ?? 0;
  if (errors === 0 && dropout === 0) {
    return `${name} has the highest remarks volume but no visible error or dropout load. The urgent operational attention is concentrated in a smaller group of high-error and high-dropout schools.`;
  }
  return `${name} leads remarks volume. Review high-error and high-dropout schools in the prioritized queue below.`;
};

export const buildSchoolSignalCards = (topSchools = []) => {
  if (!topSchools.length) return [];

  const topVolume = topSchools[0];
  const topError = [...topSchools].sort((a, b) => (b.wrong_entry ?? 0) - (a.wrong_entry ?? 0))[0];
  const topDropout = [...topSchools].sort((a, b) => (b.dropout ?? 0) - (a.dropout ?? 0))[0];
  const cleanHighVolume = topSchools.filter(
    (s) => (s.wrong_entry ?? 0) === 0 && (s.total_remarks ?? 0) >= 2000
  ).length;

  return [
    {
      id: "volume",
      title: "Highest Remarks Volume",
      value: topVolume?.total_remarks ?? 0,
      unit: "remarks",
      iconClass: "",
      dotClass: "",
      barColor: "var(--dbx-primary)",
      barWidth: 100,
      metaLeft: topVolume?.block_name ?? "—",
      metaRight: `${topVolume?.wrong_entry ?? 0} errors`,
    },
    {
      id: "errors",
      title: "Highest Error Count",
      value: topError?.wrong_entry ?? 0,
      unit: "errors",
      iconClass: "red",
      dotClass: "red",
      barColor: "var(--dbx-danger)",
      barWidth: 100,
      metaLeft: shortSchoolName(topError?.school_name, 18),
      metaRight: titleCaseBlock(topError?.block_name),
    },
    {
      id: "dropout",
      title: "Highest Dropout Signal",
      value: topDropout?.dropout ?? 0,
      unit: "remarks",
      iconClass: "orange",
      dotClass: "amber",
      barColor: "var(--dbx-secondary)",
      barWidth: topDropout?.dropout ? 100 : 0,
      metaLeft: shortSchoolName(topDropout?.school_name, 18),
      metaRight: titleCaseBlock(topDropout?.block_name),
    },
    {
      id: "clean",
      title: "Clean High-Volume Schools",
      value: cleanHighVolume,
      unit: "schools",
      iconClass: "green",
      dotClass: "",
      barColor: "var(--dbx-accent)",
      barWidth: clamp((cleanHighVolume / 20) * 100, 8, 100),
      metaLeft: "0 visible errors",
      metaRight: "Top 20",
    },
  ];
};

export const buildSchoolRankList = (topSchools = [], limit = 8) =>
  topSchools.slice(0, limit).map((school, idx) => ({
    rank: idx + 1,
    school,
    status: getSchoolStatus(school),
    total: school.total_remarks ?? 0,
  }));

const ERROR_HOTSPOT_REASONS = [
  "wrong entry/duplicate risk",
  "correction backlog",
  "high error share",
];

const DROPOUT_HOTSPOT_REASONS = ["dropout verification", "dropout follow-up"];

export const buildSchoolHotspotQueue = (topSchools = [], limit = 5) => {
  const errorSchools = [...topSchools]
    .filter((s) => (s.wrong_entry ?? 0) > 0)
    .sort((a, b) => (b.wrong_entry ?? 0) - (a.wrong_entry ?? 0))
    .slice(0, 3);
  const dropoutSchools = [...topSchools]
    .filter((s) => (s.dropout ?? 0) > 50)
    .sort((a, b) => (b.dropout ?? 0) - (a.dropout ?? 0))
    .slice(0, 2);

  const items = [
    ...errorSchools.map((school, idx) => ({
      school,
      name: school.school_name,
      block: titleCaseBlock(school.block_name),
      reason: ERROR_HOTSPOT_REASONS[idx] || "high error share",
      value: school.wrong_entry ?? 0,
      meterClass: "",
      type: "error",
    })),
    ...dropoutSchools.map((school, idx) => ({
      school,
      name: school.school_name,
      block: titleCaseBlock(school.block_name),
      reason: DROPOUT_HOTSPOT_REASONS[idx] || "dropout follow-up",
      value: school.dropout ?? 0,
      meterClass: "orange",
      type: "dropout",
    })),
  ];

  const maxVal = Math.max(...items.map((item) => item.value), 1);
  return items.slice(0, limit).map((item) => ({
    ...item,
    shortName: shortSchoolName(item.name, 35),
    widthPct: clamp((item.value / maxVal) * 100, 8, 100),
  }));
};

const TRANSITION_BAR_META = [
  { match: /academic completion/i, label: "Academic Completion", barClass: "green" },
  { match: /migration.*domestic/i, label: "Migration Domestic", barClass: "" },
  { match: /skill|iti/i, label: "Skill Education / ITI", barClass: "purple" },
  { match: /open schooling/i, label: "Open Schooling", barClass: "orange" },
  { match: /non-regular/i, label: "Non-Regular Mode", barClass: "cyan" },
  { match: /drop out/i, label: "Drop Out", barClass: "red" },
  { match: /international|country/i, label: "Migration International", barClass: "" },
  { match: /death/i, label: "Due to Death", barClass: "" },
];

export const buildTransitionBars = (transitionData = []) => {
  const rows = TRANSITION_BAR_META.map((meta) => {
    const row = transitionData.find((t) => meta.match.test(t.name || ""));
    return { ...meta, value: row?.value ?? 0 };
  }).filter((row) => row.value > 0);

  const max = Math.max(...rows.map((row) => row.value), 1);
  return rows.map((row) => ({
    ...row,
    widthPct: clamp((row.value / max) * 100, 2, 100),
    displayValue: row.value >= 1000 ? formatCompactKOne(row.value) : fmt(row.value),
  }));
};

export const buildTransitionDonut = (transitionData = []) => {
  const getVal = (pattern) => {
    const row = transitionData.find((t) => pattern.test(t.name || ""));
    return row?.value ?? 0;
  };

  const completion = getVal(/academic completion/i);
  const migration =
    getVal(/migration.*domestic/i) + getVal(/international|country/i);
  const skill = getVal(/skill|iti/i);
  const open = getVal(/open schooling/i);
  const groupedTotal = completion + migration + skill + open || 1;

  const completionPct = Math.round((completion / groupedTotal) * 100);
  const migrationPct = Math.round((migration / groupedTotal) * 100);
  const skillPct = Math.round((skill / groupedTotal) * 100);
  const openPct = Math.max(0, 100 - completionPct - migrationPct - skillPct);

  const c1 = completionPct;
  const c2 = c1 + migrationPct;
  const c3 = c2 + skillPct;

  return {
    completionPct,
    legend: [
      { label: "Academic Completion", pct: completionPct, dotClass: "" },
      { label: "Migration", pct: migrationPct, dotClass: "blue" },
      { label: "Skill / ITI", pct: skillPct, dotClass: "purple" },
      { label: "Open Schooling", pct: openPct, dotClass: "orange" },
    ],
    donutStyle: {
      background: `radial-gradient(circle at center, rgba(255,255,255,.98) 0 51%, transparent 52%), conic-gradient(#14b8a6 0 ${c1}%, #2563eb ${c1}% ${c2}%, #8b5cf6 ${c2}% ${c3}%, #ff8a1f ${c3}% 100%)`,
    },
  };
};

export const buildSchoolRankingTable = (topSchools = []) =>
  topSchools.map((school, idx) => ({
    rank: idx + 1,
    school,
    status: getSchoolStatus(school),
  }));

export const buildSchoolDeepDiveActions = (topSchools = []) => {
  const topErrors = [...topSchools]
    .filter((s) => (s.wrong_entry ?? 0) > 100)
    .sort((a, b) => (b.wrong_entry ?? 0) - (a.wrong_entry ?? 0))
    .slice(0, 5);
  const errorNames = topErrors.map((s) => shortActionSchoolName(s.school_name)).join(", ");

  const cleanSchools = topSchools.filter(
    (s) => (s.wrong_entry ?? 0) === 0 && (s.total_remarks ?? 0) >= 2500
  );

  return {
    items: [
      {
        title: "Correct Top Error Schools First",
        body: `Start with ${errorNames || "top error schools"} because they carry the largest visible data correction load.`,
        iconClass: "red",
      },
      {
        title: "Verify Dropout Remarks",
        body: "Validate dropout-heavy schools separately from error-heavy schools to avoid mixing remediation and student-tracking workflows.",
        iconClass: "orange",
      },
      {
        title: "Fast-Close Clean High-Volume Schools",
        body: `Approve or validate ${cleanSchools.length || "high-volume clean"} schools with high volume and low errors to free officer capacity for exception-heavy cases.`,
        iconClass: "green",
      },
    ],
    footer:
      "Assign school owners, set closure dates for top 5 error schools, and track dropout verification separately from data-cleaning tasks.",
  };
};

export const buildBlockPerformanceActions = (blockData = []) => {
  const sorted = sortBlocksByRemarks(blockData);
  const highError = [...blockData]
    .filter((b) => (b.error_pct ?? 0) > 15)
    .sort((a, b) => (b.error_pct ?? 0) - (a.error_pct ?? 0))
    .slice(0, 4);
  const benchmark = [...blockData]
    .filter((b) => (b.error_pct ?? 0) <= 5)
    .sort((a, b) => (b.total_remarks ?? 0) - (a.total_remarks ?? 0))[0];
  const top2 = sorted.slice(0, 2);

  return {
    items: [
      {
        title: "Start Data-Quality Correction Sprint",
        body: `Assign validation owners for ${highError.map((b) => titleCaseBlock(b.block_name)).join(", ") || "high-error blocks"} to reduce error percentages before the next review.`,
        iconClass: "red",
      },
      {
        title: "Sequence High-Volume Block Reviews",
        body: `Review ${titleCaseBlock(top2[0]?.block_name)} and ${titleCaseBlock(top2[1]?.block_name)} first because both combine high remark volume and high average remarks per school.`,
        iconClass: "",
      },
      {
        title: "Replicate Haveli Process Discipline",
        body: benchmark
          ? `Capture validation practices from ${titleCaseBlock(benchmark.block_name)} and share the checklist with blocks above 15% error.`
          : "Capture validation practices from low-error benchmark blocks and share the checklist with blocks above 15% error.",
        iconClass: "green",
      },
    ],
    footer:
      "Run weekly block huddles until error-risk blocks move below 10% and high-volume blocks clear pending remark reconciliation.",
  };
};

const GOVERNANCE_OWNER_ACTIONS = [
  "Block officer review",
  "School verification",
  "Evidence collection",
  "Retention call list",
  "School owner mapping",
  "Death remark audit",
  "Weekly follow-up",
  "Death remark validation",
  "Data validation",
  "Retention follow-up",
];

export const buildRiskGaugeStyle = (validPct = 0) => ({
  background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(#f59e0b 0 ${validPct}%, rgba(245,158,11,.14) ${validPct}% 100%)`,
  boxShadow:
    "inset 0 0 0 1px rgba(37, 99, 235, 0.14), 0 20px 42px rgba(245, 158, 11, 0.12)",
});

export const getGovernancePriority = (block = {}, rank = 1) => {
  const escalate =
    rank <= 3 ||
    (block.dropout_density ?? 0) >= 4 ||
    (block.due_to_death ?? 0) >= 90;
  return escalate
    ? { label: "Escalate", tagClass: "red" }
    : { label: "Review", tagClass: "" };
};

const getHotspotLabel = (block, rank, hotspots = []) => {
  if (rank === 1) return "Highest volume";
  const maxDensity = Math.max(...hotspots.map((h) => h.dropout_density ?? 0), 0);
  if ((block.dropout_density ?? 0) === maxDensity && rank === 2) return "Highest intensity";
  if (rank === 3) return "High risk";
  if (rank === 4) return "Field review";
  return "Follow-up";
};

export const buildRiskHeroStats = (overview = {}, dataQuality = {}, dropoutHotspots = []) => {
  const validPct = dataQuality.valid_pct ?? 0;
  const riskIndex = overview.data_risk_index ?? 0;
  const priorityCount = Math.min(dropoutHotspots.length, 10);

  return {
    validPct,
    gaugeStyle: buildRiskGaugeStyle(validPct),
    narrative:
      "Data risk is concentrated in duplicate/wrong entries and pending imports. Dropout hotspot blocks require immediate officer-level intervention and weekly closure tracking.",
    chips: [
      { text: "High Dropout Watch", className: "red" },
      { text: `${pct(riskIndex)} Data Risk Index`, className: "orange" },
      { text: `${priorityCount} Priority Blocks`, className: "blue" },
      { text: `${pct(validPct)} Validated`, className: "green" },
    ],
    kpis: [
      {
        label: "Wrong Entry / Duplicate",
        value: fmt(dataQuality.error_count),
        sub: `${pct(dataQuality.error_pct)} of total remarks`,
        cardClass: "red",
      },
      {
        label: "Pending Import",
        value: fmt(dataQuality.pending_count),
        sub: `${pct(dataQuality.pending_pct)} requiring upload action`,
        cardClass: "orange",
      },
      {
        label: "Data Risk Index",
        value: pct(riskIndex),
        sub: "Error + pending exposure",
        cardClass: "blue",
      },
      {
        label: "Clean Records",
        value: fmt(dataQuality.valid_count),
        sub: "Valid data available for reporting",
        cardClass: "green",
      },
    ],
  };
};

export const buildGovernanceSnapshotCards = (overview = {}, dataQuality = {}, dropoutHotspots = []) => {
  const topBlock = dropoutHotspots[0];
  const dropoutPct = overview.dropout_pct ?? 0;
  const exposureWidth = clamp(dropoutPct * 30, 12, 100);

  return [
    {
      id: "valid",
      title: "Validated Data",
      value: (dataQuality.valid_pct ?? 0).toFixed(1),
      unit: "% clean",
      iconClass: "green",
      dotClass: "",
      barColor: "var(--dbx-success)",
      barWidth: dataQuality.valid_pct ?? 0,
      metaLeft: `${fmt(dataQuality.valid_count)} records`,
      metaRight: "Ready for reporting",
    },
    {
      id: "errors",
      title: "Duplicate / Wrong Entry",
      value: formatCompactKOne(dataQuality.error_count ?? 0),
      unit: "records",
      iconClass: "red",
      dotClass: "red",
      barColor: "var(--dbx-danger)",
      barWidth: clamp(dataQuality.error_pct ?? 0, 4, 100),
      metaLeft: `${pct(dataQuality.error_pct)} of total`,
      metaRight: "Needs cleanup",
    },
    {
      id: "pending",
      title: "Pending Import",
      value: formatCompactKOne(dataQuality.pending_count ?? 0),
      unit: "records",
      iconClass: "orange",
      dotClass: "amber",
      barColor: "var(--dbx-secondary)",
      barWidth: clamp(dataQuality.pending_pct ?? 0, 4, 100),
      metaLeft: `${pct(dataQuality.pending_pct)} pending`,
      metaRight: "Import follow-up",
    },
    {
      id: "dropout",
      title: "Dropout Exposure",
      value: fmt(overview.dropout),
      unit: "students",
      iconClass: "purple",
      dotClass: "red",
      barColor: "var(--dbx-purple)",
      barWidth: exposureWidth,
      metaLeft: `Top block: ${titleCaseBlock(topBlock?.block_name ?? "—")}`,
      metaRight: `${fmt(topBlock?.dropout ?? 0)} cases`,
    },
  ];
};

export const buildQualityCompositionBars = (overview = {}, dataQuality = {}) => {
  const riskIndex = overview.data_risk_index ?? 0;
  return [
    {
      label: "Valid Data",
      value: dataQuality.valid_count ?? 0,
      displayValue: fmt(dataQuality.valid_count),
      barClass: "green",
      widthPct: dataQuality.valid_pct ?? 0,
    },
    {
      label: "Wrong / Duplicate",
      value: dataQuality.error_count ?? 0,
      displayValue: fmt(dataQuality.error_count),
      barClass: "red",
      widthPct: dataQuality.error_pct ?? 0,
    },
    {
      label: "Pending Import",
      value: dataQuality.pending_count ?? 0,
      displayValue: fmt(dataQuality.pending_count),
      barClass: "orange",
      widthPct: dataQuality.pending_pct ?? 0,
    },
    {
      label: "Data Risk Index",
      value: riskIndex,
      displayValue: pct(riskIndex),
      barClass: "purple",
      widthPct: riskIndex,
    },
  ];
};

export const buildGovernanceMatrix = (overview = {}, dataQuality = {}) => [
  {
    title: "Monitor",
    body: "Valid data above 80% and low pending load.",
    value: "Green",
    cellClass: "low",
  },
  {
    title: "Review",
    body: "Pending imports or duplicate records require officer follow-up.",
    value: "Amber",
    cellClass: "med",
  },
  {
    title: "Escalate",
    body: "High dropout with death remarks or repeated duplicate records.",
    value: "Red",
    cellClass: "high",
  },
  {
    title: "Import",
    body: `${fmt(dataQuality.pending_count)} pending records to be closed by data entry team.`,
    value: pct(dataQuality.pending_pct),
    cellClass: "med",
  },
  {
    title: "Cleanup",
    body: `${fmt(dataQuality.error_count)} wrong or duplicate records block clean reporting.`,
    value: pct(dataQuality.error_pct),
    cellClass: "high",
  },
  {
    title: "Retention",
    body: `${fmt(overview.dropout)} dropout cases need field validation.`,
    value: pct(overview.dropout_pct ?? 0, 1),
    cellClass: "high",
  },
];

export const buildGovernanceHotspots = (dropoutHotspots = [], limit = 10) => {
  const hotspots = dropoutHotspots.slice(0, limit);
  const maxDensity = Math.max(...hotspots.map((h) => h.dropout_density ?? 0), 0);
  const intensityBlock = hotspots.find((h) => (h.dropout_density ?? 0) === maxDensity);

  return hotspots.map((block, idx) => {
    const rank = idx + 1;
    let label = getHotspotLabel(block, rank, hotspots);
    if (intensityBlock && block.block_name === intensityBlock.block_name && rank !== 1) {
      label = "Highest intensity";
    }

    return {
      rank,
      block,
      blockLabel: (block.block_name || "").toUpperCase(),
      isRed: rank <= 3,
      metricLabel: label,
      dropout: block.dropout ?? 0,
      subtitle: `${block.total_schools} Schools · ${block.dropout_density} Dropout/school · ${block.due_to_death} Death Remarks`,
    };
  });
};

export const buildGovernanceReviewTable = (dropoutHotspots = [], limit = 10) =>
  dropoutHotspots.slice(0, limit).map((block, idx) => {
    const rank = idx + 1;
    const priority = getGovernancePriority(block, rank);
    return {
      rank,
      block,
      blockLabel: (block.block_name || "").toUpperCase(),
      priority,
      ownerAction: GOVERNANCE_OWNER_ACTIONS[idx] || "Retention follow-up",
    };
  });

export const buildGovernanceActions = (overview = {}, dataQuality = {}, dropoutHotspots = []) => {
  const top3 = dropoutHotspots.slice(0, 3).map((b) => titleCaseBlock(b.block_name)).join(", ");

  return {
    items: [
      {
        title: "Escalate Top 3 Dropout Blocks",
        body: `${top3 || "Top dropout blocks"} need field verification, school owner mapping and weekly closure tracking.`,
        iconClass: "red",
      },
      {
        title: "Close Pending Imports",
        body: `Assign data-entry owners for ${fmt(dataQuality.pending_count)} pending records and block final sign-off until import backlog reduces.`,
        iconClass: "orange",
      },
      {
        title: "Run Duplicate Cleanup Sprint",
        body: `Resolve ${fmt(dataQuality.error_count)} wrong or duplicate remarks before publishing certified dropout and transition views.`,
        iconClass: "purple",
      },
      {
        title: "Create Governance Certificate Pack",
        body: "Generate evidence pack with clean records, unresolved risks, block owners and closure remarks.",
        iconClass: "green",
      },
    ],
    footer:
      "Run a weekly Risk & Governance huddle until the data risk index drops below 10% and the top 3 dropout blocks submit verified action notes.",
  };
};

export const getTopErrorBlocks = (blockData = [], limit = 3) =>
  [...blockData].sort((a, b) => (b.error_pct ?? 0) - (a.error_pct ?? 0)).slice(0, limit);

const formatErrorBlockNames = (blocks = []) =>
  blocks.map((b) => titleCaseBlock(b.block_name)).filter(Boolean).join(", ") || "top error-rate blocks";

export const buildPrimaryExecutiveAction = (overview = {}, blockData = []) => {
  const errorBlocks = getTopErrorBlocks(blockData, 10);
  const names = formatErrorBlockNames(errorBlocks.slice(0, 3));
  const wrong = overview.wrong_entry ?? 0;
  const pending = overview.active_import ?? 0;
  if (errorBlocks.length > 0) {
    return `Prioritize ${errorBlocks.length} high-load blocks (${names}) and close ${fmt(wrong)} duplicate/wrong entries before escalating dropout follow-ups.`;
  }
  return `Close ${fmt(wrong)} duplicate/wrong entries and ${fmt(pending)} pending imports before escalating dropout follow-ups.`;
};

export const buildInsightsPrimaryNote = (overview = {}, dataQuality = {}, errorBlocks = []) => {
  const names = formatErrorBlockNames(errorBlocks);
  const errorCount = dataQuality.error_count ?? overview.wrong_entry ?? 0;
  if (!overview.schools_with_remarks) {
    return "No remarks data is available for the current scope. Import or refresh data before generating officer actions.";
  }
  if (errorCount > 0 && errorBlocks.length) {
    return `Coverage is acceptable, but the dashboard requires a focused cleanup sprint for duplicate/wrong entries and block-level verification in ${names}.`;
  }
  return "Coverage is acceptable and completion signals are strong, but continue monitoring data-quality exceptions and pending imports at block level.";
};

export const buildInsightsHeroStats = (overview = {}, dataQuality = {}, blockData = []) => {
  const errorBlocks = getTopErrorBlocks(blockData, 3);
  const riskIndex = overview.data_risk_index ?? 0;
  const errorCount = dataQuality.error_count ?? overview.wrong_entry ?? 0;
  const pendingCount = dataQuality.pending_count ?? overview.active_import ?? 0;
  const total = overview.total_remarks ?? 0;
  const errorPct =
    dataQuality.error_pct ??
    (total > 0 ? Number(((errorCount / total) * 100).toFixed(1)) : 0);
  const pendingPct =
    dataQuality.pending_pct ??
    (total > 0 ? Number(((pendingCount / total) * 100).toFixed(1)) : 0);

  return {
    chips: [
      { text: "AI Summary", className: "purple" },
      { text: `${riskIndex.toFixed(1)}% Data Risk`, className: "orange" },
      { text: `${Math.max(errorBlocks.length, 1)} Error Hotspots`, className: "red" },
      {
        text:
          overview.total_schools > 0
            ? `${overview.total_schools.toLocaleString("en-IN")} schools in scope`
            : overview.schools_with_remarks > 0
              ? `${overview.schools_with_remarks.toLocaleString("en-IN")} schools reporting`
              : "Remarks data loaded",
        className: "",
      },
    ],
    primaryInsight: buildInsightsPrimaryNote(overview, dataQuality, errorBlocks),
    kpis: [
      {
        label: "Remark Coverage",
        value: fmt(overview.schools_with_remarks),
        sub: `schools reporting · ${overview.avg_remarks_per_school ?? 0} avg/school`,
        cardClass: "good",
      },
      {
        label: "Wrong / Duplicate",
        value: fmt(errorCount),
        sub: `${errorPct.toFixed(1)}% of total remarks`,
        cardClass: "bad",
      },
      {
        label: "Pending Import",
        value: fmt(pendingCount),
        sub: `${pendingPct.toFixed(1)}% of total remarks`,
        cardClass: "warn",
      },
      {
        label: "Transition Shift",
        value: fmt(overview.iti_polytechnic ?? 0),
        sub: "ITI / Polytechnic mentions",
        cardClass: "purple",
      },
    ],
  };
};

export const buildExecutiveSummaryCards = (overview = {}, dataQuality = {}) => {
  const total = overview.total_remarks ?? 0;
  const class12 = overview.class12_passed ?? 0;
  const class12Pct = total > 0 ? Number(((class12 / total) * 100).toFixed(1)) : 0;
  const errorCount = dataQuality.error_count ?? overview.wrong_entry ?? 0;
  const pendingCount = dataQuality.pending_count ?? overview.active_import ?? 0;
  const riskIndex = overview.data_risk_index ?? 0;
  const dropout = overview.dropout ?? 0;
  const dropoutPct = overview.dropout_pct ?? (total > 0 ? Number(((dropout / total) * 100).toFixed(2)) : 0);

  return [
    {
      id: "completion",
      severity: "Stable",
      severityClass: "",
      iconClass: "green",
      title: "Completion Signal Is Strong",
      body: "Class 12 passed remarks form the largest category, showing that most records are tied to academic completion rather than unresolved dropout cases.",
      signals: [
        { value: fmt(class12), label: "Class 12 Passed Remarks" },
        { value: `${class12Pct}%`, label: "Of All Remarks" },
        { label: "Continue Completion Tracking as the Baseline Success Indicator." },
      ],
    },
    {
      id: "quality",
      severity: "High risk",
      severityClass: "red",
      iconClass: "red",
      title: "Data Quality Needs Governance",
      body: "Wrong entry and duplicate cases are large enough to distort reporting, especially when combined with pending imports.",
      signals: [
        { value: fmt(errorCount), label: "Wrong / Duplicate Entries" },
        { value: fmt(pendingCount), label: "Pending Import Records" },
        { value: `${riskIndex.toFixed(1)}%`, label: "Data Risk Index" },
      ],
    },
    {
      id: "dropout",
      severity: "Intervention",
      severityClass: "amber",
      iconClass: "orange",
      title: "Dropout Exposure Is Concentrated",
      body: "Dropout remarks are not the dominant category, but the concentration by block requires officer-level review and school follow-up.",
      signals: [
        { value: fmt(dropout), label: "Dropout Remarks" },
        { value: `${dropoutPct}%`, label: "Of Total Remarks" },
        { label: "Prioritize Top Hotspot Blocks Before Broad Campaigns." },
      ],
    },
  ];
};

export const buildRootCauseNarratives = (overview = {}, dataQuality = {}, blockData = []) => {
  const errorBlocks = getTopErrorBlocks(blockData, 3);
  const transitionTotal =
    (overview.iti_polytechnic ?? 0) +
    (overview.open_schooling ?? 0) +
    (overview.migrated_domestic ?? 0);
  const accuracy = overview.data_accuracy ?? dataQuality.valid_pct ?? 0;

  return [
    {
      id: "valid",
      iconClass: "green",
      title: "Valid Data Base Is High",
      body: "Most records are usable, but risk is still material because error and pending import volumes are not small.",
      value: `${accuracy.toFixed(1)}%`,
      valueLabel: "Accuracy",
    },
    {
      id: "errors",
      iconClass: "red",
      title: "Error Clusters Need Block Validation",
      body: `${formatErrorBlockNames(errorBlocks)} are flagged as the highest error-rate blocks.`,
      value: String(Math.max(errorBlocks.length, 1)),
      valueLabel: "Hotspots",
    },
    {
      id: "transition",
      iconClass: "orange",
      title: "Transition Pathways Require Tracking",
      body: "Skill education, open schooling and migration remarks should be reviewed as pathway shifts, not only exceptions.",
      value: fmt(transitionTotal),
      valueLabel: "Transition remarks",
    },
    {
      id: "non-regular",
      iconClass: "purple",
      title: "Non-Regular Cases Need Case Ownership",
      body: "These records may require student-level verification and follow-up routing through schools or cluster officers.",
      value: fmt(overview.non_regular ?? 0),
      valueLabel: "Students",
    },
  ];
};

export const buildSignalStrengthMatrix = (overview = {}) => {
  const rows = [
    { label: "Completion", value: overview.class12_passed ?? 0, barClass: "green" },
    { label: "Data Errors", value: overview.wrong_entry ?? 0, barClass: "red" },
    { label: "Pending Import", value: overview.active_import ?? 0, barClass: "orange" },
    { label: "Open Schooling", value: overview.open_schooling ?? 0, barClass: "" },
    { label: "ITI / Poly", value: overview.iti_polytechnic ?? 0, barClass: "purple" },
    { label: "Dropout", value: overview.dropout ?? 0, barClass: "red" },
  ];
  const max = Math.max(...rows.map((r) => r.value), 1);

  return {
    rows: rows.map((row) => ({
      ...row,
      displayValue: formatCompactKOne(row.value),
      widthPct: clamp((row.value / max) * 100, 8, 100),
    })),
    interpretation:
      "Academic completion dominates the remarks base, but the data-governance burden is significant enough to affect decision confidence unless duplicate, wrong-entry and pending-import records are cleaned at block level.",
  };
};

export const buildInsightActionQueue = (overview = {}, dataQuality = {}, blockData = []) => {
  const errorBlocks = getTopErrorBlocks(blockData, 3);
  const errorCount = dataQuality.error_count ?? overview.wrong_entry ?? 0;
  const pendingCount = dataQuality.pending_count ?? overview.active_import ?? 0;
  const blockNames = formatErrorBlockNames(errorBlocks);

  return [
    {
      priority: 1,
      theme: "Data cleanup sprint",
      evidence: `${fmt(errorCount)} wrong / duplicate entries`,
      owner: "Data Import Officer",
      action: "Run block-wise deduplication and wrong-entry validation.",
      status: "Critical",
      tagClass: "red",
    },
    {
      priority: 2,
      theme: "Pending import closure",
      evidence: `${fmt(pendingCount)} pending imports`,
      owner: "MIS Team",
      action: "Close pending import backlog with daily exception tracking.",
      status: "High",
      tagClass: "orange",
    },
    {
      priority: 3,
      theme: "Error hotspot audit",
      evidence: blockNames,
      owner: "Block Education Officers",
      action: "Verify top error-rate blocks and certify corrected data.",
      status: "Critical",
      tagClass: "red",
    },
    {
      priority: 4,
      theme: "Dropout review",
      evidence: `${fmt(overview.dropout ?? 0)} dropout remarks`,
      owner: "Retention Cell",
      action: "Validate dropout cases and assign school-level follow-up.",
      status: "High",
      tagClass: "orange",
    },
    {
      priority: 5,
      theme: "Transition pathway tracking",
      evidence: `${fmt(overview.iti_polytechnic ?? 0)} ITI / Polytechnic remarks`,
      owner: "Student Transition Team",
      action: "Track skill-education transitions as planned pathways.",
      status: "Monitor",
      tagClass: "blue",
    },
    {
      priority: 6,
      theme: "Completion benchmarking",
      evidence: `${fmt(overview.class12_passed ?? 0)} Class 12 passed remarks`,
      owner: "Academic Planning Team",
      action: "Use completion remarks as the benchmark for block reviews.",
      status: "Stable",
      tagClass: "green",
    },
  ];
};

export const buildDropboxInsightsMarkdown = (
  overview = {},
  dataQuality = {},
  blockData = []
) => {
  const errorBlocks = getTopErrorBlocks(blockData, 3);
  const primary = buildInsightsPrimaryNote(overview, dataQuality, errorBlocks);
  const cards = buildExecutiveSummaryCards(overview, dataQuality);
  const narratives = buildRootCauseNarratives(overview, dataQuality, blockData);
  const matrix = buildSignalStrengthMatrix(overview);
  const actions = buildInsightActionQueue(overview, dataQuality, blockData);
  const playbook = buildInsightsPlaybook(overview, dataQuality);

  return [
    "## Insights",
    `- ${primary}`,
    ...cards.map((c) => `- ${c.title}: ${c.body}`),
    `- ${matrix.interpretation}`,
    "",
    "## Root Cause Signals",
    ...narratives.map((n) => `- ${n.title}: ${n.body}`),
    "",
    "## Recommendations",
    ...playbook.items.map((p) => `- ${p.title}: ${p.body}`),
    "",
    "## Priority Action Items",
    ...actions.slice(0, 5).map((a) => `- ${a.theme}: ${a.action} (${a.owner})`),
    `- ${playbook.footerTitle}: ${playbook.footerBody}`,
  ].join("\n");
};

export const buildDropboxExecutiveMarkdown = (overview = {}, dataQuality = {}, blockData = []) => {
  const primary = buildPrimaryExecutiveAction(overview, blockData);
  const errorBlocks = getTopErrorBlocks(blockData, 3);
  const hero = buildHeroNarrative(overview, dataQuality) || buildInsightsPrimaryNote(overview, dataQuality, errorBlocks);

  return [
    "## Insights",
    `- ${hero}`,
    "",
    "## Priority Action Items",
    `- ${primary}`,
  ].join("\n");
};

export const buildInsightsPlaybook = (overview = {}, dataQuality = {}) => {
  const riskIndex = overview.data_risk_index ?? 0;

  return {
    items: [
      {
        title: "Escalate If Error Risk Rises",
        body: `If wrong-entry plus pending-import share remains near ${Math.round(riskIndex)}%, block-level certification should be mandatory before district review.`,
        iconClass: "red",
      },
      {
        title: "Separate True Dropout From Transition",
        body: "Do not mix dropout, open schooling and skill-education shift into one intervention bucket; they need different response models.",
        iconClass: "orange",
      },
      {
        title: "Refresh AI Insights After Cleanup",
        body: "Re-run insight generation once the cleanup sprint is complete so recommendations are based on corrected remark data.",
        iconClass: "purple",
      },
    ],
    footerTitle: "Next executive review focus",
    footerBody:
      "Ask every block to report: errors corrected, pending imports closed, dropout cases verified, and transition-pathway cases validated.",
  };
};
